import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createSvgEl, createZoomViewport } from '@learnforge/demo-render';
import { formatNumber } from '@learnforge/demo-math/numeric';
import { createAxes } from '../render/axes';
import { createDragHandle } from '../render/drag-handle';
import { createActionBar } from '../action-bar';
import type { TrigContext } from '../types';
import type { TrigKey } from '../i18n';

const BASE_VIEW = { x: -1.8, y: -1.4, width: 3.6, height: 2.8 };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type IdentitySlug =
  | 'pythagorean'
  | 'reciprocal'
  | 'quotient'
  | 'sum'
  | 'difference'
  | 'double'
  | 'half';

/** Each proof builds its DOM once; update(θ) mutates live as the user drags. */
interface ProofHandle {
  update(theta: number): void;
  destroy(): void;
}

// ---------------------------------------------------------------------------
// State — model only (no DOM refs)
// ---------------------------------------------------------------------------

interface State {
  theta: number;
  activeSlug: IdentitySlug;
}

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

/** Set multiple SVG attributes at once. */
function setAttrs(el: SVGElement, attrs: Record<string, string | number>): void {
  for (const [k, v] of Object.entries(attrs)) {
    el.setAttribute(k, String(v));
  }
}

/** Create a styled <line>. */
function makeLine(
  parent: SVGGElement,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  cls: string,
): SVGLineElement {
  const l = createSvgEl('line', { x1, y1, x2, y2, class: cls });
  parent.appendChild(l);
  return l;
}

/** Create a <rect>. */
function makeRect(
  parent: SVGGElement,
  x: number,
  y: number,
  w: number,
  h: number,
  cls: string,
): SVGRectElement {
  const r = createSvgEl('rect', { x, y, width: w, height: h, class: cls });
  parent.appendChild(r);
  return r;
}

/**
 * Create a centered <text> in SVG math-coords (y-up).
 *
 * The parent `world` group carries `transform="scale(1 -1)"` which flips the
 * coordinate system so +y points up. Without a counter-flip, text glyphs are
 * rendered upside-down. This helper negates y (to convert math→SVG coords) and
 * applies `transform="scale(1 -1)"` on the element itself so glyphs render
 * upright at the correct visual position.
 */
function makeText(
  parent: SVGGElement,
  x: number,
  y: number, // math y-up coordinate
  cls: string,
  content: string,
): SVGTextElement {
  const t = createSvgEl('text', {
    x,
    y: -y,
    transform: 'scale(1 -1)',
    'text-anchor': 'middle',
    'dominant-baseline': 'middle',
    class: cls,
  });
  t.textContent = content;
  parent.appendChild(t);
  return t;
}

/**
 * Update a text element's position using math y-up coordinates.
 * Always use this instead of setAttrs(textEl, { x, y }) to keep the
 * math-coord convention consistent with makeText.
 */
function setTextPos(el: SVGTextElement, x: number, y: number): void {
  el.setAttribute('x', String(x));
  el.setAttribute('y', String(-y));
}

// ---------------------------------------------------------------------------
// Pythagorean proof — sin²θ + cos²θ = 1
//
// Unit circle + right triangle at (cosθ, sinθ).
// sin² square: RIGHT of vertical leg — x ∈ [cosθ, cosθ+sinθ], y ∈ [0, sinθ]
// cos² square: BELOW horizontal leg — x ∈ [0, cosθ], y ∈ [-cosθ, 0]
// Summary label at bottom of figure.
// Pedagogically most meaningful in Q1 (θ ∈ [0, π/2]); absolute values used
// so the geometry degrades gracefully in other quadrants without crashing.
// ---------------------------------------------------------------------------

function buildPythagoreanProof(world: SVGGElement, ctx: TrigContext): ProofHandle {
  const group = createSvgEl('g', { class: 'tg-proof-world' });
  world.appendChild(group);

  // Unit circle
  const circle = createSvgEl('circle', { cx: 0, cy: 0, r: 1, class: 'tg-unit-circle' });
  group.appendChild(circle);

  // Right triangle: origin → (cosθ, 0) → (cosθ, sinθ)
  const triPath = createSvgEl('path', { class: 'tg-proof-triangle', d: 'M 0 0 L 1 0 L 1 0 Z' });
  group.appendChild(triPath);

  // sin² square: x ∈ [cosθ, cosθ+sinθ], y ∈ [0, sinθ] — RIGHT of vertical leg
  const sinSq = makeRect(group, 0, 0, 0, 0, 'tg-proof-sq-sin');
  const sinSqLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'sin²θ');

  // cos² square: x ∈ [0, cosθ], y ∈ [-cosθ, 0] — BELOW the x-axis
  const cosSq = makeRect(group, 0, 0, 0, 0, 'tg-proof-sq-cos');
  const cosSqLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'cos²θ');

  // Leg labels
  const sinLegLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'sin θ');
  const cosLegLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'cos θ');

  // Summary
  const summary = makeText(
    group,
    0,
    -1.25,
    'tg-proof-summary',
    ctx.i18n.t('ip.proof.pythagorean.summary'),
  );

  return {
    update(theta) {
      const s = Math.sin(theta);
      const c = Math.cos(theta);
      const absS = Math.abs(s);
      const absC = Math.abs(c);

      // Triangle path
      triPath.setAttribute('d', `M 0 0 L ${c} 0 L ${c} ${s} Z`);

      // sin² square: RIGHT of vertical leg — x ∈ [c, c+|s|], y ∈ [0, |s|]
      // Most meaningful in Q1; absolute values keep geometry valid in other quadrants.
      setAttrs(sinSq, { x: c, y: s >= 0 ? 0 : -absS, width: absS, height: absS });
      setTextPos(sinSqLabel, c + absS / 2, absS / 2);

      // cos² square: below x-axis — x ∈ [0, |c|] (shifted for Q2/Q3), y ∈ [-|c|, 0]
      setAttrs(cosSq, {
        x: c >= 0 ? 0 : c,
        y: -absC,
        width: absC,
        height: absC,
      });
      // Label at center: x = ±|c|/2, y = -|c|/2 (math y-up)
      setTextPos(cosSqLabel, (absC / 2) * (c >= 0 ? 1 : -1), -absC / 2);

      // sin leg label: slightly LEFT of the vertical leg, centred vertically.
      // Placing left of the leg avoids collision with the sin² square now on the right.
      sinLegLabel.setAttribute('text-anchor', 'end');
      setTextPos(sinLegLabel, c - 0.08, s / 2);

      // cos leg label: slightly above the horizontal leg, centred horizontally.
      setTextPos(cosLegLabel, c / 2, 0.08);

      // Keep summary position stable (y already set by makeText at construction)
      setAttrs(summary, { x: 0 });
    },
    destroy() {
      group.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Reciprocal proof — csc = 1/sin, sec = 1/cos, cot = 1/tan
//
// Three pairs of segments on the unit circle, driven by shared θ.
// For each pair: primary segment + labelled 1/primary value.
// ---------------------------------------------------------------------------

function buildReciprocalProof(world: SVGGElement, ctx: TrigContext): ProofHandle {
  const group = createSvgEl('g', { class: 'tg-proof-world' });
  world.appendChild(group);

  // Unit circle
  const circle = createSvgEl('circle', { cx: 0, cy: 0, r: 1, class: 'tg-unit-circle' });
  group.appendChild(circle);

  // Radius line
  const radiusLine = makeLine(group, 0, 0, 1, 0, 'tg-proof-radius');

  // sin segment: (cosθ, 0) → (cosθ, sinθ) — vertical
  const sinSeg = makeLine(group, 0, 0, 0, 0, 'tg-segment-sin');
  const sinLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'sin θ');
  const cscLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'csc = 1/sin');

  // cos segment: (0,0) → (cosθ, 0) — horizontal
  const cosSeg = makeLine(group, 0, 0, 0, 0, 'tg-segment-cos');
  const cosLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'cos θ');
  const secLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'sec = 1/cos');

  // tan segment: (1, 0) → (1, tanθ)
  const tanSeg = makeLine(group, 1, 0, 1, 0, 'tg-proof-tan-seg');
  const tanLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'tan θ');
  const cotLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'cot = 1/tan');

  // Summary
  const summary = makeText(
    group,
    0,
    -1.25,
    'tg-proof-summary',
    ctx.i18n.t('ip.proof.reciprocal.summary'),
  );

  return {
    update(theta) {
      const s = Math.sin(theta);
      const c = Math.cos(theta);
      const t = Math.abs(c) > 0.01 ? s / c : s > 0 ? 50 : -50;

      // Radius
      setAttrs(radiusLine, { x2: c, y2: s });

      // sin segment
      setAttrs(sinSeg, { x1: c, y1: 0, x2: c, y2: s });
      setTextPos(sinLabel, c + (c >= 0 ? 0.1 : -0.1), s / 2);
      sinLabel.setAttribute('text-anchor', c >= 0 ? 'start' : 'end');
      setTextPos(cscLabel, c + (c >= 0 ? 0.1 : -0.1), s / 2 - 0.1);
      cscLabel.setAttribute('text-anchor', c >= 0 ? 'start' : 'end');

      // cos segment
      setAttrs(cosSeg, { x1: 0, y1: 0, x2: c, y2: 0 });
      setTextPos(cosLabel, c / 2, 0.13);
      setTextPos(secLabel, c / 2, 0.23);

      // tan segment (clamped)
      const tClamped = Math.max(-3, Math.min(3, t));
      setAttrs(tanSeg, { x1: 1, y1: 0, x2: 1, y2: tClamped });
      const tMid = tClamped / 2;
      setTextPos(tanLabel, 1.12, tMid);
      tanLabel.setAttribute('text-anchor', 'start');
      setTextPos(cotLabel, 1.12, tMid - 0.1);
      cotLabel.setAttribute('text-anchor', 'start');

      // Summary stays fixed
      setAttrs(summary, { x: 0 });
    },
    destroy() {
      group.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Quotient proof — tan θ = sin θ / cos θ
//
// Shows unit circle + right triangle + extended tangent line.
// The similar-triangle relationship: inner triangle (base cosθ, height sinθ)
// is similar to outer triangle (base 1, height tanθ). tan = sinθ/cosθ.
// ---------------------------------------------------------------------------

function buildQuotientProof(world: SVGGElement, ctx: TrigContext): ProofHandle {
  const group = createSvgEl('g', { class: 'tg-proof-world' });
  world.appendChild(group);

  const circle = createSvgEl('circle', { cx: 0, cy: 0, r: 1, class: 'tg-unit-circle' });
  group.appendChild(circle);

  // x = 1 vertical reference line
  makeLine(group, 1, -1.3, 1, 1.3, 'tg-proof-radius');

  // radius
  const radiusLine = makeLine(group, 0, 0, 1, 0, 'tg-proof-radius');

  // Inner right triangle: origin → (cosθ,0) → (cosθ, sinθ)
  const innerTri = createSvgEl('path', { class: 'tg-proof-triangle', d: 'M 0 0 L 1 0 L 1 0 Z' });
  group.appendChild(innerTri);

  // Outer similar triangle (larger): origin → (1, 0) → (1, tanθ)
  const outerTri = createSvgEl('path', { class: 'tg-proof-tan-tri', d: 'M 0 0 L 1 0 L 1 0 Z' });
  group.appendChild(outerTri);

  // sin segment vertical
  const sinSeg = makeLine(group, 0, 0, 0, 0, 'tg-segment-sin');
  const sinLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'sin θ');

  // cos segment horizontal
  const cosSeg = makeLine(group, 0, 0, 0, 0, 'tg-segment-cos');
  const cosLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'cos θ');

  // tan segment on x=1 line
  const tanSeg = makeLine(group, 1, 0, 1, 0, 'tg-proof-tan-seg');
  const tanLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'tan θ');

  // Equation label
  const eqLabel = makeText(
    group,
    0,
    -1.25,
    'tg-proof-summary',
    ctx.i18n.t('ip.proof.quotient.summary'),
  );

  return {
    update(theta) {
      const s = Math.sin(theta);
      const c = Math.cos(theta);
      const tVal = Math.abs(c) > 0.01 ? s / c : s > 0 ? 50 : -50;
      const tClamped = Math.max(-3, Math.min(3, tVal));

      setAttrs(radiusLine, { x2: c, y2: s });
      innerTri.setAttribute('d', `M 0 0 L ${c} 0 L ${c} ${s} Z`);
      outerTri.setAttribute('d', `M 0 0 L 1 0 L 1 ${tClamped} Z`);

      setAttrs(sinSeg, { x1: c, y1: 0, x2: c, y2: s });
      setTextPos(sinLabel, c + (c >= 0 ? 0.1 : -0.1), s / 2);
      sinLabel.setAttribute('text-anchor', c >= 0 ? 'start' : 'end');

      setAttrs(cosSeg, { x1: 0, y1: 0, x2: c, y2: 0 });
      setTextPos(cosLabel, c / 2, 0.13);

      setAttrs(tanSeg, { x1: 1, y1: 0, x2: 1, y2: tClamped });
      setTextPos(tanLabel, 1.12, tClamped / 2);
      tanLabel.setAttribute('text-anchor', 'start');

      setAttrs(eqLabel, { x: 0 });
    },
    destroy() {
      group.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Double-angle proof — sin 2θ = 2 sin θ cos θ
//
// Shows two radii: one at θ, one at 2θ. Drops a vertical from (cos2θ, sin2θ)
// to x-axis showing sin(2θ). Annotates the doubled angle.
// ---------------------------------------------------------------------------

function buildDoubleAngleProof(world: SVGGElement, ctx: TrigContext): ProofHandle {
  const group = createSvgEl('g', { class: 'tg-proof-world' });
  world.appendChild(group);

  const circle = createSvgEl('circle', { cx: 0, cy: 0, r: 1, class: 'tg-unit-circle' });
  group.appendChild(circle);

  // radius at θ
  const r1 = makeLine(group, 0, 0, 1, 0, 'tg-proof-radius-theta');
  // dot at θ
  const dot1 = createSvgEl('circle', { cx: 1, cy: 0, r: 0.05, class: 'tg-proof-dot' });
  group.appendChild(dot1);
  const thetaLabel = makeText(group, 0, 0, 'tg-proof-leg-label', 'θ');

  // radius at 2θ
  const r2 = makeLine(group, 0, 0, 1, 0, 'tg-proof-radius-2theta');
  // dot at 2θ
  const dot2 = createSvgEl('circle', { cx: 1, cy: 0, r: 0.05, class: 'tg-proof-dot' });
  group.appendChild(dot2);
  const twothetaLabel = makeText(group, 0, 0, 'tg-proof-leg-label', '2θ');

  // Vertical drop from (cos2θ, sin2θ) to x-axis
  const sin2Seg = makeLine(group, 0, 0, 0, 0, 'tg-segment-sin');
  const sin2Label = makeText(group, 0, 0, 'tg-proof-leg-label', 'sin 2θ');

  // Annotation: sin 2θ = 2·sinθ·cosθ
  const annot = makeText(group, 0, 0, 'tg-proof-annot', '');

  const summary = makeText(
    group,
    0,
    -1.25,
    'tg-proof-summary',
    ctx.i18n.t('ip.proof.double.summary'),
  );

  return {
    update(theta) {
      const s = Math.sin(theta);
      const c = Math.cos(theta);
      const s2 = Math.sin(2 * theta);
      const c2 = Math.cos(2 * theta);

      setAttrs(r1, { x2: c, y2: s });
      setAttrs(dot1, { cx: c, cy: s });
      // θ label: midpoint of radius at θ, offset outward (math y-up)
      setTextPos(thetaLabel, c * 0.35, s * 0.35 + 0.1);

      setAttrs(r2, { x2: c2, y2: s2 });
      setAttrs(dot2, { cx: c2, cy: s2 });
      // 2θ label at midpoint of r2 radius (math y-up)
      setTextPos(twothetaLabel, c2 * 0.55, s2 * 0.55 + 0.1);

      setAttrs(sin2Seg, { x1: c2, y1: 0, x2: c2, y2: s2 });
      setTextPos(sin2Label, c2 + (c2 >= 0 ? 0.1 : -0.1), s2 / 2);
      sin2Label.setAttribute('text-anchor', c2 >= 0 ? 'start' : 'end');

      // annotation row — clamped display (math y-up: 1.1 is above centre)
      annot.textContent = `sin 2θ = 2·${s.toFixed(3)}·${c.toFixed(3)} = ${s2.toFixed(3)}`;
      setTextPos(annot, 0, 1.1);

      setAttrs(summary, { x: 0 });
    },
    destroy() {
      group.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Arc helper — SVG path `d` for a unit-circle arc from alpha to beta at radius r
// ---------------------------------------------------------------------------

function unitArcD(alpha: number, beta: number, r = 1): string {
  const x0 = r * Math.cos(alpha);
  const y0 = r * Math.sin(alpha);
  const x1 = r * Math.cos(beta);
  const y1 = r * Math.sin(beta);
  const sweep = beta > alpha ? 1 : 0;
  const large = Math.abs(beta - alpha) > Math.PI ? 1 : 0;
  return `M ${x0} ${y0} A ${r} ${r} 0 ${large} ${sweep} ${x1} ${y1}`;
}

// ---------------------------------------------------------------------------
// Sum proof — sin(A+B) = sin A cos B + cos A sin B
//
// A is fixed at π/6 (30°). B is variable; user drags θ = A+B.
// Shows two arcs (A in faint, B in accent) plus the vertical drop = sin(A+B).
// ---------------------------------------------------------------------------

function buildSumProof(world: SVGGElement, _ctx: TrigContext): ProofHandle {
  const A = Math.PI / 6; // fixed at 30°

  const group = createSvgEl('g', { class: 'tg-proof-world' });
  world.appendChild(group);

  // Unit circle
  const circle = createSvgEl('circle', { cx: 0, cy: 0, r: 1, class: 'tg-unit-circle' });
  group.appendChild(circle);

  // Arc for fixed angle A (0 → A) — faint
  const arcA = createSvgEl('path', { class: 'tg-proof-arc-faint', d: unitArcD(0, A, 0.25) });
  group.appendChild(arcA);

  // Arc for variable angle B (A → θ) — accent
  const arcB = createSvgEl('path', { class: 'tg-proof-arc-accent', d: unitArcD(A, A, 0.35) });
  group.appendChild(arcB);

  // Radius at θ
  const radius = makeLine(group, 0, 0, 1, 0, 'tg-proof-radius');

  // Vertical drop from (cos θ, sin θ) to (cos θ, 0) — represents sin(A+B)
  const drop = makeLine(group, 0, 0, 0, 0, 'tg-segment-sin');

  // Dot at (cos θ, sin θ)
  const dot = createSvgEl('circle', { cx: 1, cy: 0, r: 0.05, class: 'tg-proof-dot' });
  group.appendChild(dot);

  // Labels: A arc label, B arc label
  const labelA = makeText(group, 0, 0, 'tg-proof-leg-label', 'A');
  const labelB = makeText(group, 0, 0, 'tg-proof-leg-label', 'B');

  // Numeric summary rows (math y-up: below the unit circle)
  const row1 = makeText(group, 0, -1.05, 'tg-proof-summary', '');
  const row2 = makeText(group, 0, -1.2, 'tg-proof-annot', '');
  const row3 = makeText(group, 0, -1.35, 'tg-proof-annot', '');

  return {
    update(theta) {
      const B = theta - A;
      const sA = Math.sin(A);
      const cA = Math.cos(A);
      const sB = Math.sin(B);
      const cB = Math.cos(B);
      const sAB = Math.sin(theta); // sin(A+B)
      const cT = Math.cos(theta);
      const sT = Math.sin(theta);

      // Arcs
      arcA.setAttribute('d', unitArcD(0, A, 0.25));
      arcB.setAttribute('d', unitArcD(A, theta, 0.35));

      // Radius and drop
      setAttrs(radius, { x1: 0, y1: 0, x2: cT, y2: sT });
      setAttrs(drop, { x1: cT, y1: 0, x2: cT, y2: sT });
      setAttrs(dot, { cx: cT, cy: sT });

      // Arc labels at midpoint of each arc
      const midA = A / 2;
      setTextPos(labelA, 0.18 * Math.cos(midA), 0.18 * Math.sin(midA));
      const midB = A + B / 2;
      setTextPos(labelB, 0.45 * Math.cos(midB), 0.45 * Math.sin(midB));

      // Numeric rows
      row1.textContent = `sin(A+B) = ${formatNumber(sAB, { decimals: 2 })}`;
      row2.textContent = `= sinA cosB + cosA sinB`;
      row3.textContent = `= ${formatNumber(sA, { decimals: 2 })}·${formatNumber(cB, { decimals: 2 })} + ${formatNumber(cA, { decimals: 2 })}·${formatNumber(sB, { decimals: 2 })} = ${formatNumber(sA * cB + cA * sB, { decimals: 2 })}`;
    },
    destroy() {
      group.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Difference proof — sin(A−B) = sin A cos B − cos A sin B
//
// A is fixed at π/3 (60°). θ is the result angle A−B; user drags it.
// B = A − θ. Arc from 0→A in faint, arc from θ back to A in accent (reversed).
// ---------------------------------------------------------------------------

function buildDifferenceProof(world: SVGGElement, _ctx: TrigContext): ProofHandle {
  const A = Math.PI / 3; // fixed at 60°

  const group = createSvgEl('g', { class: 'tg-proof-world' });
  world.appendChild(group);

  // Unit circle
  const circle = createSvgEl('circle', { cx: 0, cy: 0, r: 1, class: 'tg-unit-circle' });
  group.appendChild(circle);

  // Arc for fixed angle A (0 → A) — faint
  const arcA = createSvgEl('path', { class: 'tg-proof-arc-faint', d: unitArcD(0, A, 0.25) });
  group.appendChild(arcA);

  // Arc from θ to A in accent — represents B (going backwards = subtraction)
  const arcB = createSvgEl('path', { class: 'tg-proof-arc-accent', d: unitArcD(0, A, 0.35) });
  group.appendChild(arcB);

  // Radius at angle θ (= A−B)
  const radius = makeLine(group, 0, 0, 1, 0, 'tg-proof-radius');

  // Vertical drop from (cos θ, sin θ) to (cos θ, 0) — represents sin(A−B)
  const drop = makeLine(group, 0, 0, 0, 0, 'tg-segment-sin');

  // Dot at (cos θ, sin θ)
  const dot = createSvgEl('circle', { cx: 1, cy: 0, r: 0.05, class: 'tg-proof-dot' });
  group.appendChild(dot);

  // Arc labels
  const labelA = makeText(group, 0, 0, 'tg-proof-leg-label', 'A');
  const labelB = makeText(group, 0, 0, 'tg-proof-leg-label', 'B');

  // Numeric summary rows
  const row1 = makeText(group, 0, -1.05, 'tg-proof-summary', '');
  const row2 = makeText(group, 0, -1.2, 'tg-proof-annot', '');
  const row3 = makeText(group, 0, -1.35, 'tg-proof-annot', '');

  return {
    update(theta) {
      const B = A - theta; // B = A − θ  (θ is the result angle)
      const sA = Math.sin(A);
      const cA = Math.cos(A);
      const sB = Math.sin(B);
      const cB = Math.cos(B);
      const sAB = Math.sin(theta); // sin(A−B)
      const cT = Math.cos(theta);
      const sT = Math.sin(theta);

      // Arcs — arcA stays fixed; arcB goes from theta up to A (backwards sweep)
      arcA.setAttribute('d', unitArcD(0, A, 0.25));
      // theta → A: when theta < A this is counterclockwise (forward), but visually
      // we show the gap that was "removed", so it's drawn from theta to A.
      arcB.setAttribute('d', unitArcD(theta, A, 0.35));

      // Radius and drop
      setAttrs(radius, { x1: 0, y1: 0, x2: cT, y2: sT });
      setAttrs(drop, { x1: cT, y1: 0, x2: cT, y2: sT });
      setAttrs(dot, { cx: cT, cy: sT });

      // Arc labels
      const midA = A / 2;
      setTextPos(labelA, 0.18 * Math.cos(midA), 0.18 * Math.sin(midA));
      // B arc midpoint is between theta and A
      const midB = (theta + A) / 2;
      setTextPos(labelB, 0.45 * Math.cos(midB), 0.45 * Math.sin(midB));

      // Numeric rows
      row1.textContent = `sin(A−B) = ${formatNumber(sAB, { decimals: 2 })}`;
      row2.textContent = `= sinA cosB − cosA sinB`;
      row3.textContent = `= ${formatNumber(sA, { decimals: 2 })}·${formatNumber(cB, { decimals: 2 })} − ${formatNumber(cA, { decimals: 2 })}·${formatNumber(sB, { decimals: 2 })} = ${formatNumber(sA * cB - cA * sB, { decimals: 2 })}`;
    },
    destroy() {
      group.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Half-angle proof — sin(θ/2) = √((1 − cos θ)/2)  for θ ∈ [0, 2π]
//
// Full arc 0→θ in accent. Half arc 0→θ/2 in faint.
// Radius at θ (full) and at θ/2 (half). Horizontal segment 0→cosθ on x-axis.
// Red dashed segment cosθ→1 showing (1−cosθ). Vertical drop at θ/2.
// ---------------------------------------------------------------------------

function buildHalfAngleProof(world: SVGGElement, _ctx: TrigContext): ProofHandle {
  const group = createSvgEl('g', { class: 'tg-proof-world' });
  world.appendChild(group);

  // Unit circle
  const circle = createSvgEl('circle', { cx: 0, cy: 0, r: 1, class: 'tg-unit-circle' });
  group.appendChild(circle);

  // Arc for full angle θ — accent
  const arcFull = createSvgEl('path', { class: 'tg-proof-arc-accent', d: 'M 1 0' });
  group.appendChild(arcFull);

  // Arc for half angle θ/2 — faint
  const arcHalf = createSvgEl('path', { class: 'tg-proof-arc-faint', d: 'M 1 0' });
  group.appendChild(arcHalf);

  // Radius at θ (full angle)
  const radiusFull = makeLine(group, 0, 0, 1, 0, 'tg-proof-radius');

  // Radius at θ/2 (half angle) — lighter style
  const radiusHalf = makeLine(group, 0, 0, 1, 0, 'tg-proof-radius-half');

  // Horizontal segment: origin to (cos θ, 0) — represents cos θ
  const segCos = makeLine(group, 0, 0, 1, 0, 'tg-proof-segment-cos');

  // Red dashed segment: (cos θ, 0) to (1, 0) — represents 1−cos θ
  const segOneMinus = makeLine(group, 0, 0, 0, 0, 'tg-proof-segment-one-minus-cos');

  // Vertical drop from (cos(θ/2), sin(θ/2)) to x-axis — sin(θ/2)
  const dropHalf = makeLine(group, 0, 0, 0, 0, 'tg-segment-sin');

  // Dot at (cos θ, sin θ)
  const dotFull = createSvgEl('circle', { cx: 1, cy: 0, r: 0.05, class: 'tg-proof-dot' });
  group.appendChild(dotFull);

  // Dot at (cos θ/2, sin θ/2)
  const dotHalf = createSvgEl('circle', { cx: 1, cy: 0, r: 0.04, class: 'tg-proof-dot' });
  group.appendChild(dotHalf);

  // Labels
  const labelFull = makeText(group, 0, 0, 'tg-proof-leg-label', 'θ');
  const labelHalf = makeText(group, 0, 0, 'tg-proof-leg-label', 'θ/2');

  // Numeric summary rows
  const row1 = makeText(group, 0, -1.05, 'tg-proof-summary', '');
  const row2 = makeText(group, 0, -1.2, 'tg-proof-annot', '');
  const row3 = makeText(group, 0, -1.35, 'tg-proof-annot', '');

  return {
    update(theta) {
      const half = theta / 2;
      const cT = Math.cos(theta);
      const sT = Math.sin(theta);
      const cH = Math.cos(half);
      const sH = Math.sin(half);

      // Arcs
      arcFull.setAttribute('d', unitArcD(0, theta, 0.35));
      arcHalf.setAttribute('d', unitArcD(0, half, 0.25));

      // Radii
      setAttrs(radiusFull, { x1: 0, y1: 0, x2: cT, y2: sT });
      setAttrs(radiusHalf, { x1: 0, y1: 0, x2: cH, y2: sH });

      // Horizontal segment 0 → cos θ
      setAttrs(segCos, { x1: 0, y1: 0, x2: cT, y2: 0 });

      // Red dashed: cos θ → 1
      setAttrs(segOneMinus, { x1: cT, y1: 0, x2: 1, y2: 0 });

      // Vertical drop at θ/2
      setAttrs(dropHalf, { x1: cH, y1: 0, x2: cH, y2: sH });

      // Dots
      setAttrs(dotFull, { cx: cT, cy: sT });
      setAttrs(dotHalf, { cx: cH, cy: sH });

      // Labels — midpoints of arcs, offset slightly outward
      setTextPos(labelFull, 0.47 * Math.cos(theta / 2), 0.47 * Math.sin(theta / 2));
      setTextPos(labelHalf, 0.18 * Math.cos(half / 2), 0.18 * Math.sin(half / 2));

      // sin(θ/2): always ≥ 0 for θ ∈ [0, 2π] since θ/2 ∈ [0, π]
      const sinHalf = Math.abs(sH);
      const rhs = Math.sqrt(Math.max(0, (1 - cT) / 2));

      // Numeric rows
      row1.textContent = `sin(θ/2) = ${formatNumber(sinHalf, { decimals: 2 })}`;
      row2.textContent = `= √((1 − cosθ) / 2)`;
      row3.textContent = `= √((1 − ${formatNumber(cT, { decimals: 2 })}) / 2) = ${formatNumber(rhs, { decimals: 2 })}`;
    },
    destroy() {
      group.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// PROOF REGISTRY
// ---------------------------------------------------------------------------

interface IdentityProof {
  slug: IdentitySlug;
  build(world: SVGGElement, ctx: TrigContext): ProofHandle;
}

const PROOFS: Record<IdentitySlug, IdentityProof> = {
  pythagorean: { slug: 'pythagorean', build: buildPythagoreanProof },
  reciprocal: { slug: 'reciprocal', build: buildReciprocalProof },
  quotient: { slug: 'quotient', build: buildQuotientProof },
  double: { slug: 'double', build: buildDoubleAngleProof },
  sum: { slug: 'sum', build: buildSumProof },
  difference: { slug: 'difference', build: buildDifferenceProof },
  half: { slug: 'half', build: buildHalfAngleProof },
};

const IDENTITY_ORDER: IdentitySlug[] = [
  'pythagorean',
  'reciprocal',
  'quotient',
  'sum',
  'difference',
  'double',
  'half',
];

// ---------------------------------------------------------------------------
// Spec factory
// ---------------------------------------------------------------------------

function buildSpec(): CustomExperimentSpec<State, TrigKey, TrigContext> {
  return {
    labelKey: 'experiment.identity-playground.label',
    demoId: 'trigonometry',
    advancedKey: 'identity-playground',

    initialState(): State {
      return {
        theta: Math.PI / 4,
        activeSlug: 'pythagorean',
      };
    },

    createRender(ctx, host, controls) {
      // ---- i18n subscription tracking ----
      const i18nUnsubs: Array<() => void> = [];

      function trackSubscribe(callback: () => void): void {
        const off = ctx.i18n.subscribe(callback);
        i18nUnsubs.push(off);
      }

      function clearI18nSubscriptions(): void {
        for (const off of i18nUnsubs) off();
        i18nUnsubs.length = 0;
      }

      // ---- Transient render state ----
      let activeProof: ProofHandle | null = null;
      let builtSlug: IdentitySlug | null = null;

      // ---- SVG stage — tighter viewBox, font-size scaled to viewBox height ----
      // viewBox: -1.8 -1.4 3.6 2.8  → height=2.8, font-size ≈ 2.8/40 = 0.07
      const svg = createSvgEl('svg', {
        viewBox: `${BASE_VIEW.x} ${BASE_VIEW.y} ${BASE_VIEW.width} ${BASE_VIEW.height}`,
        preserveAspectRatio: 'xMidYMid meet',
        class: 'tg-svg',
        'font-size': '0.07',
      });
      host.appendChild(svg);

      // Y-flip so mathematical coordinates point up
      const world = createSvgEl('g', { transform: 'scale(1 -1)' });
      svg.appendChild(world);

      // Axes — just show ±1 ticks, no 2/3
      const axes = createAxes({
        xDomain: [-1.8, 1.8],
        yDomain: [-1.4, 1.4],
        targetTicks: 3,
        i18n: ctx.i18n,
      });
      world.appendChild(axes.group);

      // ---- Draggable point on the unit circle (shared across proofs) ----
      // The drag-handle is added to `world` (y-flipped), so its cy is in math coords.
      // We put it in a separate group on top of proof geometry.
      const handleGroup = createSvgEl('g', { class: 'tg-proof-handle-layer' });
      world.appendChild(handleGroup);

      const handle = createDragHandle(handleGroup, {
        cx: Math.cos(Math.PI / 4),
        cy: Math.sin(Math.PI / 4),
        r: 0.08,
        snap(x, y) {
          // Project onto unit circle
          const len = Math.hypot(x, y) || 1;
          return { x: x / len, y: y / len };
        },
        onMove({ x, y }) {
          controls.setState((s) => ({ ...s, theta: Math.atan2(y, x) }));
        },
      });

      const viewport = createZoomViewport(svg, {
        base: BASE_VIEW,
        yUp: true,
        fontSize: 0.07,
        onChange: (view) => {
          axes.update({ xDomain: view.xDomain, yDomain: view.yDomain, chromeScale: 1 / view.zoom });
          handle.setScale(1 / view.zoom);
        },
      });

      const actionBarHandle = createActionBar(ctx.actionBar, {
        buttons: [{ id: 'view', labelKey: 'view.reset', onClick: () => viewport.reset() }],
        i18n: ctx.i18n,
      });

      // ---- Panel — build identity list ONCE ----
      for (const slug of IDENTITY_ORDER) {
        const btn = document.createElement('button');
        btn.className = 'tg-identity-row';
        btn.dataset.slug = slug;
        const labelKey = `ip.id.${slug}` as TrigKey;
        btn.textContent = ctx.i18n.t(labelKey);
        trackSubscribe(() => {
          btn.textContent = ctx.i18n.t(labelKey);
        });
        btn.addEventListener('click', () => {
          controls.setState((s) => ({ ...s, activeSlug: slug }));
        });
        ctx.panel.appendChild(btn);
      }

      // ---------------------------------------------------------------------------
      // update — reconcile view from state
      // ---------------------------------------------------------------------------
      function update(state: State): void {
        // ---- Proof switching ----
        if (state.activeSlug !== builtSlug) {
          // Destroy the outgoing proof
          activeProof?.destroy();
          activeProof = null;

          // Build new proof into world (before handleGroup so handle stays on top)
          activeProof = PROOFS[state.activeSlug].build(world, ctx);

          // Re-append handleGroup so drag handle stays on top of new proof geometry
          world.appendChild(handleGroup);

          builtSlug = state.activeSlug;
        }

        // ---- Reposition drag handle ----
        handle.setPosition(Math.cos(state.theta), Math.sin(state.theta));

        // ---- Update active-button highlight ----
        for (const btn of ctx.panel.querySelectorAll<HTMLButtonElement>('.tg-identity-row')) {
          btn.dataset.active = btn.dataset.slug === state.activeSlug ? 'true' : 'false';
        }

        // ---- Drive proof geometry ----
        activeProof?.update(state.theta);
      }

      return {
        update,
        dispose() {
          activeProof?.destroy();
          activeProof = null;
          handle.destroy();
          axes.destroy();
          clearI18nSubscriptions();
          viewport.dispose();
          actionBarHandle.destroy();
          svg.remove();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Alias for the existing index.ts + test imports (behaviorally identical).
export const mountIdentityPlayground = experiment.mount.bind(experiment);
